<script setup>
import { useSlots, computed } from 'vue'

const $slots = useSlots()
const emit = defineEmits(['click'])

const { $openNewWindow } = useNuxtApp()

const props = defineProps({
  tag: {
    type: String,
    default: 'button',
  },

  type: {
    type: String,
    default: 'button',
    validator: prop => ['button', 'submit'].includes(prop),
  },

  variant: {
    type: String,
    default: 'default',
    validator: prop => ['default', 'primary', 'secondary', 'caution', 'error', 'transparent', 'outline'].includes(prop),
  },

  size: {
    type: String,
    default: 'md',
    validator: prop => ['xs', 'sm', 'md', 'lg', 'xl'].includes(prop),
  },

  to: {
    type: String,
    default: null,
  },

  isLoading: {
    type: Boolean,
    default: false,
  },

  isDisabled: {
    type: Boolean,
    default: false,
  },

  // Overrides prefixIcon
  prefixText: {
    type: String,
    default: null,
  },

  prefixIcon: {
    type: String,
    default: null,
  },

  suffixText: {
    type: String,
    default: null,
  },

  suffixIcon: {
    type: String,
    default: null,
  },

  tooltip: {
    type: String,
    default: null,
  },

  openInNewTab: {
    type: Boolean,
    default: false,
  },
})

const compIsPrimary = computed(() => {
  return props.priority === 'primary'
})

const compClass = computed(() => {
  return [
    {
      [props.priority]: true,
      [props.size]: true,
      loading: props.isLoading,
      disabled: props.isDisabled || props.isLoading,
      'icon-only': props.prefixIcon && !$slots.default,
    },
    compIsPrimary.value ? 'text-white' : 'text-body',
  ]
})

const openInNewTab = computed(() => {
  return props.openInNewTab || (typeof props.to === 'string' && props.to.includes('https://'))
})

const hasPrefix = computed(() => {
  return !!props.prefixIcon || !!props.prefixText || !!$slots.prefix
})

const hasSuffix = computed(() => {
  return !!props.suffixIcon || !!props.suffixText || !!$slots.suffix
})

function onButtonClick() {
  emit('click')

  if (props.to && props.to.includes('tel:')) {
    document.location.href = props.to
  } else if (openInNewTab.value) {
    $openNewWindow(props.to)
  } else if (props.to) {
    navigateTo(props.to)
  }
}
</script>

<template>
  <component
    :is="tag"
    class="flex items-center space-x-1 button temp-border"
    :type="type"
    :class="compClass"
    :disabled="isDisabled || isLoading"
    :title="tooltip"
    @click="onButtonClick"
  >
    <!-- @slot Custom loading content, otherwise a spinner. Loading replaces prefix -->
    <slot
      v-if="isLoading"
      name="loading"
    >
      <Loader iconOnly />
    </slot>

    <span
      v-else-if="hasPrefix"
      class="icon-prefix"
    >
      <slot name="prefix">
        <template v-if="prefixText">{{ prefixText }}</template>

        <Icon
          v-else-if="prefixIcon"
          :name="prefixIcon"
          class="!text-current"
        />
      </slot>
    </span>

    <!-- @slot Used for the button label -->
    <span
      v-if="$slots.default"
      class="capitalize-first-letter"
    >
      <slot />
    </span>

    <span
      v-if="hasSuffix"
      class="icon-suffix"
    >
      <slot name="suffix">
        <template v-if="suffixText">
          <span>{{ suffixText }}</span>
        </template>

        <Icon
          v-else-if="suffixIcon"
          :name="suffixIcon"
          class="suffix-icon"
          fixedWidth
        />
      </slot>
    </span>
  </component>
</template>

<style lang="postcss" scoped>
@reference "../../assets/index.css";

.button {
  @apply items-center bg-black justify-center  font-medium border border-transparent select-none focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500;
  transition: background-color 0.2s ease;

  &:not(.transparent):not([disabled]):not(.disabled):not(.caution):not(.error) {
    @apply shadow-sm focus:ring-primary;
  }
}

.button.xs {
  @apply px-2 py-1 text-xs leading-3;

  & .icon-prefix {
    @apply -ml-0.5 mr-1;
  }

  & .icon-suffix {
    @apply ml-1 -mr-0.5;
  }
}

.button.sm {
  @apply px-2.5 py-1.5 text-sm leading-4;

  & .icon-prefix {
    @apply -ml-0.5 mr-0.5;
  }

  & .icon-suffix {
    @apply ml-2 -mr-0.5;
  }
}

.button.md {
  @apply px-4 py-2 text-sm min-h-[44px] sm:min-h-[44px];

  & .icon-prefix {
    @apply mr-2 -ml-1;
  }

  & .icon-suffix {
    @apply ml-2 -mr-1;
  }
}

.button.lg {
  @apply px-4 py-2;

  & .icon-prefix {
    @apply mr-3 -ml-1;
  }

  & .icon-suffix {
    @apply ml-3 -mr-1;
  }
}

.button.xl {
  @apply px-6 py-3;

  & .icon-prefix {
    @apply mr-3 -ml-1;
  }

  & .icon-suffix {
    @apply ml-3 -mr-1;
  }
}

.button.primary {
  @apply bg-primary text-white;

  &:not([disabled]):not(.disabled):hover {
    @apply bg-primary-700;
  }

  &.disabled:not(:focus),
  &:disabled:not(:focus) {
    cursor: not-allowed;
  }
}

.button.default {
  @apply text-body bg-white  hover:bg-gray-100 focus:ring-primary border-gray-300;

  .suffix-icon,
  .prefix-icon {
    @apply text-body;
  }
}

.button.secondary {
  @apply text-white bg-secondary focus:ring-secondary-200 hover:bg-secondary-800;
}

.button.caution {
  @apply text-red-700 shadow-none bg-transparent hover:bg-red-50 focus:ring-red-200;

  .suffix-icon {
    @apply text-red-700;
  }
}

.button.error {
  @apply text-white bg-error focus:ring-red-200 hover:bg-red-800;
}

.button.black {
  @apply text-white bg-black focus:ring-black hover:bg-gray-900;
}

.button.selected {
  @apply text-white bg-green-600 hover:bg-green-700 focus:ring-green-700;
}

.button.selected .suffix-icon {
  @apply text-white;
}

.button.disabled {
  @apply text-gray-400 border-gray-400 cursor-default !;
  background-color: #f8fafc!;
}

.button.disabled .suffix-icon {
  @apply text-gray-400;
}

.button.caution.disabled,
.button.transparent.disabled {
  @apply border-transparent;
  background-color: transparent!;
}

.button.transparent {
  @apply border-transparent bg-transparent hover:opacity-80 text-primary-600;
}
</style>
